import { focusKey, type FocusItem } from '@ehr/shared';

/**
 * ehr#682 (PROGRESS_NOTE_10-20-V141 toggleFocus, addFocusLabel, removeFocus): what a tap, "+ Add"
 * and Remove do to the note's Problems, goals, and objectives addressed. `on` is what the note
 * saves as picked (body.focusAddressed); `off` the keys taken off this note only (body.focusOff).
 * Plain functions so the section can be tested without the editor.
 */
export interface FocusState {
  on: FocusItem[];
  off: string[];
}

const strip = (c: FocusItem): FocusItem => ({ key: c.key, label: c.label, kind: c.kind });

/** A tap: picked goes off, not picked goes on. */
export function toggleFocus(s: FocusState, c: FocusItem): FocusState {
  return s.on.some((x) => x.key === c.key)
    ? { ...s, on: s.on.filter((x) => x.key !== c.key) }
    : { ...s, on: [...s.on, strip(c)] };
}

/**
 * "+ Add": the item is picked on this note (one already offered, plan or own, is picked as it is;
 * new words are the clinician's own item). `wasOn` when it was picked already (V114: say so).
 */
export function addFocus(
  s: FocusState,
  choices: readonly FocusItem[],
  label: string,
): FocusState & { item: FocusItem; wasOn: boolean } {
  const words = label.replace(/\s+/g, ' ').trim();
  const key = focusKey(words);
  const found = choices.find((c) => c.key === key) ?? s.on.find((c) => c.key === key);
  const item = found ? strip(found) : { key, label: words, kind: 'custom' as const };
  const wasOn = s.on.some((x) => x.key === key);
  return {
    on: wasOn ? s.on : [...s.on, item],
    off: s.off.filter((k) => k !== key),
    item,
    wasOn,
  };
}

/**
 * An EHR-written plan's problem chip: its Presenting problem (a free-text box), its first line,
 * cut to chip length. None when the box is empty.
 */
export function ehrPlanProblems(presenting: string | undefined): string[] {
  const first = (presenting ?? '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+/g, ' ').trim())
    .find(Boolean);
  if (!first) return [];
  return [first.length > 120 ? `${first.slice(0, 117).trimEnd()}...` : first];
}

/** Remove: off this note; This note only also keeps it out of this note's chips. */
export function removeFocus(
  s: FocusState,
  key: string,
  scope: 'note' | 'client' | 'all',
): FocusState {
  return {
    on: s.on.filter((x) => x.key !== key),
    off: scope === 'note' && !s.off.includes(key) ? [...s.off, key] : s.off,
  };
}
